import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { showBlockingError } from '@/stores/blockingErrorStore';

/**
 * Centralized error message parser for consistent user-facing error messages.
 * Extracts meaningful messages from API errors and provides fallbacks.
 */
export const getFriendlyErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    // 1. Check for backend custom message (most specific)
    const serverMessage = error.response?.data?.message;
    if (serverMessage && typeof serverMessage === 'string') {
      return serverMessage;
    }

    // 2. Check for validation errors array
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      if (Array.isArray(errors)) {
        return errors.map((e: any) => e.message || e).join(', ');
      }
      if (typeof errors === 'object') {
        return Object.values(errors).flat().join(', ');
      }
    }

    // 3. Check for error field (some APIs use this)
    if (error.response?.data?.error && typeof error.response.data.error === 'string') {
      return error.response.data.error;
    }

    // 4. HTTP status code fallbacks
    switch (error.response?.status) {
      case 400:
        return 'Invalid request. Please check your inputs.';
      case 401:
        return 'Session expired. Please login again.';
      case 403:
        return "You don't have permission to perform this action.";
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This action conflicts with existing data.';
      case 422:
        return 'Validation failed. Please check your inputs.';
      case 429:
        return 'Too many requests. Please wait a moment.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again.';
      default:
        break;
    }

    // 5. Network error
    if (error.code === 'ERR_NETWORK' || !error.response) {
      return 'Network error. Please check your connection.';
    }
  }

  // 6. Standard Error object
  if (error instanceof Error) {
    return error.message;
  }

  // 7. String error
  if (typeof error === 'string') {
    return error;
  }

  // 8. Ultimate fallback
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Extract error message for logging (may include more technical details)
 */
export const getErrorForLogging = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const url = error.config?.url;
    const method = error.config?.method?.toUpperCase();
    const message = getFriendlyErrorMessage(error);
    return `[${method} ${url}] ${status}: ${message}`;
  }

  if (error instanceof Error) {
    return error.stack || error.message;
  }

  return String(error);
};

/**
 * Check if an error should be displayed as a blocking modal.
 * An error is blocking if the API response includes:
 * - blocking: true
 * - severity: "critical"
 */
export const isBlockingError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    if (!data) return false;

    // Check for explicit blocking flag
    if (data.blocking === true) return true;

    // Check for critical severity
    if (data.severity === 'critical') return true;
  }

  return false;
};

/**
 * Get the user-facing message from an error, preferring userMessage if available.
 * This is used for blocking errors where we want the most human-readable message.
 */
export const getBlockingErrorMessage = (error: unknown, fallback?: string): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data;

    // Prefer userMessage for blocking errors (most human-readable)
    if (data?.userMessage && typeof data.userMessage === 'string') {
      return data.userMessage;
    }
  }

  // Fall back to standard error message extraction
  return getFriendlyErrorMessage(error) || fallback || 'An unexpected error occurred.';
};

/**
 * Unified error notification handler.
 * Routes critical/blocking errors to the blocking modal, others to toast.
 *
 * Usage:
 * ```ts
 * try {
 *   await someApiCall();
 * } catch (error) {
 *   notifyError(error, 'Failed to save changes');
 * }
 * ```
 *
 * @param error - The error to handle
 * @param fallbackMessage - Optional fallback message if error has no readable message
 * @param forceBlocking - Optional flag to force blocking behavior regardless of error payload
 */
export const notifyError = (
  error: unknown,
  fallbackMessage?: string,
  forceBlocking = false
): void => {
  const shouldBlock = forceBlocking || isBlockingError(error);
  const message = shouldBlock
    ? getBlockingErrorMessage(error, fallbackMessage)
    : getFriendlyErrorMessage(error) || fallbackMessage || 'An unexpected error occurred.';

  if (shouldBlock) {
    // Show blocking modal for critical errors
    showBlockingError(message, 'Action Required');
  } else {
    // Show standard toast for non-critical errors
    toast.error(message);
  }
};
